import { useState, useEffect, useRef, useCallback } from "react";
import { supabase, HeroBannerRow } from "../../../lib/supabase";
import ImageUploader from "../../../components/base/ImageUploader";
import BannerUploader, { FocalPoint, focalToCssPosition } from "../../../components/base/BannerUploader";

type Lang = "vi" | "kr";
interface Props { lang: Lang; }

const FONTS = [
  { label: "Mặc định", value: "inherit" },
  { label: "Inter", value: "Inter, sans-serif" },
  { label: "Roboto", value: "Roboto, sans-serif" },
  { label: "Montserrat", value: "Montserrat, sans-serif" },
  { label: "Playfair Display", value: "Playfair Display, serif" },
  { label: "Noto Serif", value: "Noto Serif, serif" },
  { label: "Bebas Neue", value: "Bebas Neue, sans-serif" },
  { label: "Oswald", value: "Oswald, sans-serif" },
  { label: "Georgia", value: "Georgia, serif" },
  { label: "Arial Black", value: "Arial Black, sans-serif" },
];

const OBJECT_POSITIONS = [
  "center top", "center center", "center bottom",
  "left top", "left center", "right top", "right center",
];

interface EditForm {
  image_url: string;
  slogan_vi: string;
  slogan_kr: string;
  sub_vi: string;
  sub_kr: string;
  display_order: number;
  is_active: boolean;
  slogan_color: string;
  slogan_font_family: string;
  slogan_font_size: number;
  slogan_white_space: string;
  slogan_x: number;
  slogan_y: number;
  slogan_letter_spacing: number;
  slogan_line_height: number;
  image_object_fit: string;
  image_object_position: string;
}

const defaultForm = (): EditForm => ({
  image_url: "",
  slogan_vi: "",
  slogan_kr: "",
  sub_vi: "",
  sub_kr: "",
  display_order: 1,
  is_active: true,
  slogan_color: "#ffffff",
  slogan_font_family: "inherit",
  slogan_font_size: 36,
  slogan_white_space: "nowrap",
  slogan_x: 10,
  slogan_y: 15,
  slogan_letter_spacing: 0,
  slogan_line_height: 1.2,
  image_object_fit: "cover",
  image_object_position: "center top",
});

// ── Visual Editor Component ──
interface VisualEditorProps {
  lang: Lang;
  editData?: HeroBannerRow | null;
  onClose: () => void;
  onSuccess: () => void;
  nextOrder: number;
}

const VisualEditor: React.FC<VisualEditorProps> = ({ lang, editData, onClose, onSuccess, nextOrder }) => {
  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);
  const [form, setForm] = useState<EditForm>(defaultForm());
  const [images, setImages] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState<"slogan" | "image" | "meta">("slogan");
  const [isDragging, setIsDragging] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; sloganX: number; sloganY: number } | null>(null);

  useEffect(() => {
    if (editData) {
      setForm({
        image_url: editData.image_url ?? "",
        slogan_vi: editData.slogan_vi ?? "",
        slogan_kr: editData.slogan_kr ?? "",
        sub_vi: editData.sub_vi ?? "",
        sub_kr: editData.sub_kr ?? "",
        display_order: editData.display_order ?? 1,
        is_active: editData.is_active ?? true,
        slogan_color: editData.slogan_color ?? "#ffffff",
        slogan_font_family: editData.slogan_font_family ?? "inherit",
        slogan_font_size: editData.slogan_font_size ?? 36,
        slogan_white_space: editData.slogan_white_space ?? "nowrap",
        slogan_x: editData.slogan_x ?? 10,
        slogan_y: editData.slogan_y ?? 15,
        slogan_letter_spacing: editData.slogan_letter_spacing ?? 0,
        slogan_line_height: editData.slogan_line_height ?? 1.2,
        image_object_fit: editData.image_object_fit ?? "cover",
        image_object_position: editData.image_object_position ?? "center top",
      });
      if (editData.image_url) setImages([editData.image_url]);
    } else {
      setForm((p) => ({ ...p, display_order: nextOrder }));
    }
  }, [editData, nextOrder]);

  const set = useCallback(<K extends keyof EditForm>(field: K, val: EditForm[K]) => {
    setForm((p) => ({ ...p, [field]: val }));
  }, []);

  // Drag to position slogan
  const handleSloganMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      sloganX: form.slogan_x,
      sloganY: form.slogan_y,
    };
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !dragStartRef.current || !previewRef.current) return;
      const rect = previewRef.current.getBoundingClientRect();
      const dx = ((e.clientX - dragStartRef.current.mouseX) / rect.width) * 100;
      const dy = ((e.clientY - dragStartRef.current.mouseY) / rect.height) * 100;
      const newX = Math.max(0, Math.min(85, dragStartRef.current.sloganX + dx));
      const newY = Math.max(0, Math.min(80, dragStartRef.current.sloganY + dy));
      setForm((p) => ({ ...p, slogan_x: Math.round(newX * 10) / 10, slogan_y: Math.round(newY * 10) / 10 }));
    };
    const handleMouseUp = () => { setIsDragging(false); dragStartRef.current = null; };
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const handleSave = async () => {
    const imgUrl = images[0] || form.image_url;
    if (!imgUrl) { setError(t("Vui lòng upload ảnh slide!", "슬라이드 이미지를 업로드해주세요!")); return; }
    if (!form.slogan_vi.trim()) { setError(t("Vui lòng nhập slogan tiếng Việt!", "베트남어 슬로건을 입력해주세요!")); return; }
    setSaving(true);
    setError("");
    const payload = { ...form, image_url: imgUrl, updated_at: new Date().toISOString() };
    const { error: e } = editData?.id
      ? await supabase.from("hero_banners").update(payload).eq("id", editData.id)
      : await supabase.from("hero_banners").insert(payload);
    setSaving(false);
    if (e) { setError(e.message); return; }
    onSuccess();
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-[11px] font-semibold text-gray-500 mb-1 uppercase tracking-wide";
  const imgUrl = images[0] || form.image_url;
  const sloganText = lang === "vi" ? (form.slogan_vi || "Câu slogan của bạn") : (form.slogan_kr || form.slogan_vi || "슬로건 텍스트");

  const tabs = [
    { id: "slogan" as const, label: t("Slogan", "슬로건"), icon: "ri-text" },
    { id: "image" as const, label: t("Ảnh Banner", "배너 이미지"), icon: "ri-image-2-line" },
    { id: "meta" as const, label: t("Cài đặt", "설정"), icon: "ri-settings-3-line" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-[#F8F9FA] rounded-2xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-white border-b border-gray-200 rounded-t-2xl flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#004A99]/10 flex items-center justify-center">
              <i className="ri-magic-line text-[#004A99] text-sm" />
            </div>
            <div>
              <h2 className="font-bold text-gray-800 text-sm">
                {editData ? t("Visual Editor – Chỉnh sửa Slide", "Visual Editor – 슬라이드 수정") : t("Visual Editor – Slide mới", "Visual Editor – 새 슬라이드")}
              </h2>
              <p className="text-xs text-gray-400">{t("Kéo câu slogan để đặt vị trí trực tiếp trên banner", "슬로건을 드래그하여 배너 위에 직접 위치를 지정하세요")}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Left: Preview Area */}
          <div className="flex-1 p-5 flex flex-col gap-4 overflow-y-auto">
            <div className="bg-white rounded-xl border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t("Xem trước Banner (kéo slogan để di chuyển)", "배너 미리보기 (슬로건을 드래그하여 이동)")}</p>
                <span className="text-[10px] text-[#004A99] bg-[#004A99]/8 px-2 py-0.5 rounded font-medium">
                  X: {form.slogan_x.toFixed(1)}% &nbsp; Y: {form.slogan_y.toFixed(1)}%
                </span>
              </div>

              {/* Preview Canvas */}
              <div
                ref={previewRef}
                className="relative rounded-xl overflow-hidden bg-[#001428]"
                style={{ paddingTop: "30.2%", cursor: isDragging ? "grabbing" : "default" }}
              >
                <div className="absolute inset-0">
                  {imgUrl ? (
                    <img
                      src={imgUrl}
                      alt="preview"
                      className="w-full h-full"
                      style={{
                        objectFit: form.image_object_fit as "cover" | "contain",
                        objectPosition: form.image_object_position,
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#001428] to-[#004A99]">
                      <div className="text-center text-white/40">
                        <i className="ri-image-2-line text-4xl block mb-2" />
                        <p className="text-xs">{t("Upload ảnh để xem trước", "이미지를 업로드하여 미리보기")}</p>
                      </div>
                    </div>
                  )}

                  {/* Gradients */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#001428]/90 via-[#001428]/40 to-[#001428]/15 pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#001428]/75 via-[#001428]/25 to-transparent pointer-events-none" />

                  {/* Draggable Slogan */}
                  <div
                    className="absolute z-10 select-none"
                    style={{
                      left: `${form.slogan_x}%`,
                      top: `${form.slogan_y}%`,
                      cursor: isDragging ? "grabbing" : "grab",
                    }}
                    onMouseDown={handleSloganMouseDown}
                  >
                    {/* Drag hint ring */}
                    <div
                      className="absolute -inset-2 rounded-lg border-2 border-dashed border-white/40 pointer-events-none transition-opacity"
                      style={{ opacity: isDragging ? 1 : 0 }}
                    />

                    <div className="flex items-stretch gap-1.5">
                      <div className="w-[2px] bg-gradient-to-b from-[#00AEEF] to-[#004A99] rounded-full flex-shrink-0" />
                      <div
                        style={{
                          color: form.slogan_color,
                          fontFamily: form.slogan_font_family !== "inherit" ? form.slogan_font_family : undefined,
                          fontSize: `${Math.round(form.slogan_font_size * 0.45)}px`,
                          whiteSpace: form.slogan_white_space as "nowrap" | "normal",
                          letterSpacing: `${form.slogan_letter_spacing * 0.45}px`,
                          lineHeight: form.slogan_line_height,
                          fontWeight: 900,
                          textShadow: "0 1px 8px rgba(0,0,0,0.9)",
                          maxWidth: "45%",
                        }}
                      >
                        {sloganText}
                      </div>
                    </div>

                    {/* Drag handle icon */}
                    <div className="absolute -top-3 -right-3 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md">
                      <i className="ri-drag-move-line text-[10px] text-[#004A99]" />
                    </div>
                  </div>

                  {/* Korean Brief box placeholder */}
                  <div className="absolute right-2 top-1 bottom-1 w-[28%] border border-white/20 rounded-lg bg-[#001428]/60 flex items-center justify-center pointer-events-none">
                    <span className="text-white/30 text-[9px] font-bold text-center leading-tight">Korean Brief<br/>Panel</span>
                  </div>
                </div>
              </div>

              {/* Arrow controls for fine positioning */}
              <div className="mt-3 flex items-center gap-3">
                <p className="text-xs text-gray-500 mr-1">{t("Di chuyển:", "이동:")}</p>
                <div className="flex items-center gap-1">
                  {[
                    { icon: "ri-arrow-up-line", dx: 0, dy: -1 },
                    { icon: "ri-arrow-down-line", dx: 0, dy: 1 },
                    { icon: "ri-arrow-left-line", dx: -1, dy: 0 },
                    { icon: "ri-arrow-right-line", dx: 1, dy: 0 },
                  ].map(({ icon, dx, dy }) => (
                    <button
                      key={icon}
                      onClick={() => setForm((p) => ({
                        ...p,
                        slogan_x: Math.max(0, Math.min(85, p.slogan_x + dx)),
                        slogan_y: Math.max(0, Math.min(80, p.slogan_y + dy)),
                      }))}
                      className="w-7 h-7 flex items-center justify-center bg-gray-100 hover:bg-[#004A99] hover:text-white rounded-lg transition-colors cursor-pointer text-gray-600"
                    >
                      <i className={`${icon} text-xs`} />
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <label className="text-xs text-gray-500">X:</label>
                  <input type="number" value={form.slogan_x} onChange={(e) => set("slogan_x", Math.max(0, Math.min(85, parseFloat(e.target.value) || 0)))}
                    className="w-16 border border-gray-200 rounded px-2 py-1 text-xs text-center outline-none" step={0.5} min={0} max={85} />
                  <label className="text-xs text-gray-500">Y:</label>
                  <input type="number" value={form.slogan_y} onChange={(e) => set("slogan_y", Math.max(0, Math.min(80, parseFloat(e.target.value) || 0)))}
                    className="w-16 border border-gray-200 rounded px-2 py-1 text-xs text-center outline-none" step={0.5} min={0} max={80} />
                </div>
              </div>
            </div>

            {/* Error */}
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}
          </div>

          {/* Right: Settings Panel */}
          <div className="w-80 bg-white border-l border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
            {/* Tabs */}
            <div className="flex border-b border-gray-200 flex-shrink-0">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex flex-col items-center gap-1 py-3 text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap border-b-2 ${
                    activeTab === tab.id
                      ? "text-[#004A99] border-[#004A99] bg-[#004A99]/3"
                      : "text-gray-400 border-transparent hover:text-gray-600"
                  }`}
                >
                  <i className={`${tab.icon} text-base`} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* ── SLOGAN TAB ── */}
              {activeTab === "slogan" && (
                <>
                  <div>
                    <label className={labelCls}>{t("Slogan tiếng Việt", "베트남어 슬로건")} *</label>
                    <textarea
                      className={`${inputCls} resize-none`}
                      rows={2}
                      value={form.slogan_vi}
                      onChange={(e) => set("slogan_vi", e.target.value)}
                      placeholder={t("Công nghệ Hàn - Động lực Việt", "한국 기술 – 베트남의 성장 동력")}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>{t("Slogan tiếng Hàn", "한국어 슬로건")}</label>
                    <textarea
                      className={`${inputCls} resize-none`}
                      rows={2}
                      value={form.slogan_kr}
                      onChange={(e) => set("slogan_kr", e.target.value)}
                      placeholder="한국 기술 – 베트남의 성장 동력"
                    />
                  </div>

                  <div className="h-px bg-gray-100" />

                  {/* Color */}
                  <div>
                    <label className={labelCls}>{t("Màu chữ", "글자색")}</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={form.slogan_color}
                        onChange={(e) => set("slogan_color", e.target.value)}
                        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer p-0.5 bg-white"
                      />
                      <div className="flex gap-1 flex-wrap">
                        {["#ffffff", "#FFD700", "#00AEEF", "#FF6B35", "#A8E6CF", "#FFB3C1"].map((c) => (
                          <button
                            key={c}
                            onClick={() => set("slogan_color", c)}
                            className="w-6 h-6 rounded-full border-2 cursor-pointer transition-transform hover:scale-110"
                            style={{
                              backgroundColor: c,
                              borderColor: form.slogan_color === c ? "#004A99" : "transparent",
                            }}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-gray-400 font-mono">{form.slogan_color}</span>
                    </div>
                  </div>

                  {/* Font */}
                  <div>
                    <label className={labelCls}>{t("Font chữ", "글꼴")}</label>
                    <select
                      value={form.slogan_font_family}
                      onChange={(e) => set("slogan_font_family", e.target.value)}
                      className={inputCls}
                      style={{ fontFamily: form.slogan_font_family !== "inherit" ? form.slogan_font_family : undefined }}
                    >
                      {FONTS.map((f) => (
                        <option key={f.value} value={f.value} style={{ fontFamily: f.value !== "inherit" ? f.value : undefined }}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Font size */}
                  <div>
                    <label className={labelCls}>{t("Cỡ chữ", "글자 크기")}: <span className="text-[#004A99] font-bold">{form.slogan_font_size}px</span></label>
                    <input
                      type="range" min={16} max={80} step={1}
                      value={form.slogan_font_size}
                      onChange={(e) => set("slogan_font_size", parseInt(e.target.value))}
                      className="w-full accent-[#004A99]"
                    />
                    <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                      <span>16px</span><span>48px</span><span>80px</span>
                    </div>
                  </div>

                  {/* Letter spacing */}
                  <div>
                    <label className={labelCls}>{t("Khoảng cách chữ", "자간")}: <span className="text-[#004A99] font-bold">{form.slogan_letter_spacing}px</span></label>
                    <input
                      type="range" min={-5} max={20} step={0.5}
                      value={form.slogan_letter_spacing}
                      onChange={(e) => set("slogan_letter_spacing", parseFloat(e.target.value))}
                      className="w-full accent-[#004A99]"
                    />
                  </div>

                  {/* Line height */}
                  <div>
                    <label className={labelCls}>{t("Khoảng cách dòng", "줄 간격")}: <span className="text-[#004A99] font-bold">{form.slogan_line_height}</span></label>
                    <input
                      type="range" min={0.8} max={3} step={0.1}
                      value={form.slogan_line_height}
                      onChange={(e) => set("slogan_line_height", parseFloat(e.target.value))}
                      className="w-full accent-[#004A99]"
                    />
                  </div>

                  {/* Word wrap */}
                  <div>
                    <label className={labelCls}>{t("Chế độ xuống dòng", "줄 바꿈")}</label>
                    <div className="flex gap-2">
                      {[
                        { val: "nowrap", label: t("Không xuống dòng", "줄 바꿈 없음"), icon: "ri-text" },
                        { val: "normal", label: t("Tự xuống dòng", "자동 줄 바꿈"), icon: "ri-paragraph" },
                      ].map(({ val, label, icon }) => (
                        <button
                          key={val}
                          onClick={() => set("slogan_white_space", val)}
                          className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg border-2 transition-colors cursor-pointer ${
                            form.slogan_white_space === val
                              ? "border-[#004A99] bg-[#004A99]/8 text-[#004A99]"
                              : "border-gray-200 text-gray-500 hover:border-gray-300"
                          }`}
                        >
                          <i className={`${icon} text-sm`} /> {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="h-px bg-gray-100" />
                  <div>
                    <label className={labelCls}>{t("Mô tả tiếng Việt", "베트남어 설명")}</label>
                    <textarea className={`${inputCls} resize-none`} rows={2} value={form.sub_vi} onChange={(e) => set("sub_vi", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>{t("Mô tả tiếng Hàn", "한국어 설명")}</label>
                    <textarea className={`${inputCls} resize-none`} rows={2} value={form.sub_kr} onChange={(e) => set("sub_kr", e.target.value)} />
                  </div>
                </>
              )}

              {/* ── IMAGE TAB ── */}
              {activeTab === "image" && (
                <>
                  <BannerUploader
                    imageUrl={images[0] || form.image_url}
                    focalPoint={{ x: form.slogan_x ?? 50, y: form.slogan_y ?? 50 }}
                    onChange={(url, focal) => {
                      if (url) {
                        setImages([url]);
                        set("image_url", url);
                      } else {
                        setImages([]);
                        set("image_url", "");
                      }
                      set("image_object_position", `${focal.x}% ${focal.y}%`);
                    }}
                    aspectRatio={21 / 9}
                    folder="hero-banners"
                    label={t("Ảnh Hero Banner (tỷ lệ 21:9 · Khuyến nghị >1920px)", "Hero 배너 이미지 (21:9 비율 · 1920px 이상 권장)")}
                    previewLabel={t("Xem trước Banner", "배너 미리보기")}
                  />

                  <div className="h-px bg-gray-100" />

                  <div>
                    <label className={labelCls}>{t("Kiểu hiển thị ảnh", "이미지 표시 방식")}</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { val: "cover", label: t("Lấp đầy (Cover)", "꽉 채우기"), icon: "ri-aspect-ratio-fill" },
                        { val: "contain", label: t("Hiển thị đầy đủ (Contain)", "전체 표시"), icon: "ri-aspect-ratio-line" },
                      ].map(({ val, label, icon }) => (
                        <button
                          key={val}
                          onClick={() => set("image_object_fit", val)}
                          className={`flex flex-col items-center gap-1 p-3 text-[10px] font-semibold rounded-lg border-2 transition-colors cursor-pointer ${
                            form.image_object_fit === val
                              ? "border-[#004A99] bg-[#004A99]/8 text-[#004A99]"
                              : "border-gray-200 text-gray-500 hover:border-gray-300"
                          }`}
                        >
                          <i className={`${icon} text-lg`} /> {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ── META TAB ── */}
              {activeTab === "meta" && (
                <>
                  <div>
                    <label className={labelCls}>{t("Thứ tự hiển thị", "표시 순서")}</label>
                    <input
                      type="number"
                      className={inputCls}
                      value={form.display_order}
                      onChange={(e) => set("display_order", parseInt(e.target.value) || 1)}
                      min={1}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
                    <div>
                      <p className="text-xs font-semibold text-gray-700">{t("Hiển thị trên trang chủ", "홈페이지에 표시")}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{t("Bật/tắt slide này", "이 슬라이드를 켜거나 끄세요")}</p>
                    </div>
                    <button
                      onClick={() => set("is_active", !form.is_active)}
                      className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${form.is_active ? "bg-[#004A99]" : "bg-gray-300"}`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.is_active ? "right-1" : "left-1"}`} />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Save */}
            <div className="p-4 border-t border-gray-200 flex-shrink-0 space-y-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-bold rounded-xl cursor-pointer disabled:opacity-50 transition-colors"
              >
                {saving ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                {saving ? t("Đang lưu...", "저장 중...") : t("Lưu & Cập nhật trang chủ", "저장 및 홈 업데이트")}
              </button>
              <button onClick={onClose} className="w-full py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-xl cursor-pointer transition-colors">
                {t("Hủy", "취소")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Manager ──
const HeroBannerManager: React.FC<Props> = ({ lang }) => {
  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);
  const [banners, setBanners] = useState<HeroBannerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editItem, setEditItem] = useState<HeroBannerRow | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchBanners = async () => {
    setLoading(true);
    const { data } = await supabase.from("hero_banners").select("*").order("display_order", { ascending: true });
    setBanners((data as HeroBannerRow[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { fetchBanners(); }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("Xác nhận xóa slide này?", "이 슬라이드를 삭제하시겠습니까?"))) return;
    setDeletingId(id);
    await supabase.from("hero_banners").delete().eq("id", id);
    setDeletingId(null);
    fetchBanners();
  };

  const toggleActive = async (item: HeroBannerRow) => {
    setTogglingId(item.id);
    await supabase.from("hero_banners").update({ is_active: !item.is_active }).eq("id", item.id);
    setTogglingId(null);
    fetchBanners();
  };

  const moveOrder = async (item: HeroBannerRow, direction: "up" | "down") => {
    const idx = banners.findIndex((b) => b.id === item.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= banners.length) return;
    const swapItem = banners[swapIdx];
    await Promise.all([
      supabase.from("hero_banners").update({ display_order: swapItem.display_order }).eq("id", item.id),
      supabase.from("hero_banners").update({ display_order: item.display_order }).eq("id", swapItem.id),
    ]);
    fetchBanners();
  };

  const nextOrder = banners.length > 0 ? Math.max(...banners.map((b) => b.display_order)) + 1 : 1;

  return (
    <div>
      {showEditor && (
        <VisualEditor
          lang={lang}
          editData={editItem}
          nextOrder={nextOrder}
          onClose={() => { setShowEditor(false); setEditItem(null); }}
          onSuccess={() => { setShowEditor(false); setEditItem(null); fetchBanners(); }}
        />
      )}

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{t("Quản lý Hero Banner", "Hero 배너 관리")}</h1>
          <p className="text-sm text-gray-500 mt-1">{t("Visual Editor Canva-style – kéo thả slogan, chỉnh màu, font, vị trí trực tiếp", "Visual Editor – 슬로건 드래그, 색상·폰트·위치 직접 편집")}</p>
        </div>
        <button
          onClick={() => { setEditItem(null); setShowEditor(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-xl hover:bg-[#003a7a] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" />
          {t("Thêm Slide mới", "새 슬라이드 추가")}
        </button>
      </div>

      {/* Info tip */}
      <div className="bg-gradient-to-r from-[#004A99]/8 to-[#00AEEF]/8 border border-[#004A99]/15 rounded-xl p-4 mb-5 flex items-start gap-3">
        <i className="ri-magic-line text-[#004A99] text-lg mt-0.5" />
        <div>
          <p className="text-xs font-bold text-[#004A99]">{t("Visual Editor Canva-Style", "Visual Editor Canva-Style")}</p>
          <p className="text-xs text-[#004A99]/70 mt-0.5">
            {t(
              "Bấm nút ✏️ để mở Visual Editor. Kéo câu slogan đến bất kỳ vị trí nào. Chỉnh màu, font, cỡ chữ, khoảng cách. Nhấn Lưu → trang chủ cập nhật ngay.",
              "✏️ 버튼을 클릭하여 Visual Editor를 여세요. 슬로건을 어디든 드래그하세요. 색상, 폰트, 크기를 조정하세요. 저장 클릭 → 홈페이지 즉시 업데이트."
            )}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        {[
          { label: t("Tổng slides", "전체"), value: banners.length, color: "bg-[#004A99]", icon: "ri-slideshow-2-line" },
          { label: t("Đang hiển thị", "활성"), value: banners.filter((b) => b.is_active).length, color: "bg-green-600", icon: "ri-eye-line" },
          { label: t("Đang ẩn", "비활성"), value: banners.filter((b) => !b.is_active).length, color: "bg-gray-400", icon: "ri-eye-off-line" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className={`w-8 h-8 ${s.color} rounded-lg flex items-center justify-center mb-2`}>
              <i className={`${s.icon} text-white text-sm`} />
            </div>
            <p className="text-xl font-black text-gray-800">{s.value}</p>
            <p className="text-xs text-gray-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Banner list */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-[#004A99]" />
          </div>
        ) : banners.length === 0 ? (
          <div className="text-center py-16">
            <i className="ri-image-2-line text-3xl text-gray-300 block mb-3" />
            <p className="text-sm text-gray-400">{t("Chưa có slide nào. Thêm slide mới để bắt đầu.", "슬라이드가 없습니다. 새 슬라이드를 추가하세요.")}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {banners.map((banner, idx) => (
              <div key={banner.id} className="flex items-center gap-4 p-4 hover:bg-[#F8F9FA] transition-colors">
                {/* Order controls */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button onClick={() => moveOrder(banner, "up")} disabled={idx === 0}
                    className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 cursor-pointer">
                    <i className="ri-arrow-up-s-line text-xs" />
                  </button>
                  <span className="text-[10px] text-gray-400 text-center font-bold">{banner.display_order}</span>
                  <button onClick={() => moveOrder(banner, "down")} disabled={idx === banners.length - 1}
                    className="w-6 h-6 flex items-center justify-center rounded bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 cursor-pointer">
                    <i className="ri-arrow-down-s-line text-xs" />
                  </button>
                </div>

                {/* Preview */}
                <div className="relative w-40 h-24 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                  <img src={banner.image_url} alt={banner.slogan_vi} className="w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
                  {/* Slogan dot indicator position */}
                  <div
                    className="absolute w-2 h-2 rounded-full border border-white/80"
                    style={{
                      left: `${Math.min(85, banner.slogan_x ?? 10)}%`,
                      top: `${Math.min(80, banner.slogan_y ?? 15)}%`,
                      backgroundColor: banner.slogan_color ?? "#ffffff",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                  <p className="absolute bottom-2 left-2 right-2 text-white text-[8px] font-bold line-clamp-2 leading-tight">{banner.slogan_vi}</p>
                  {!banner.is_active && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <span className="text-white text-[10px] font-bold bg-gray-700/80 px-2 py-1 rounded whitespace-nowrap">{t("Đang ẩn", "숨김")}</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${banner.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {banner.is_active ? t("Hiển thị", "표시") : t("Ẩn", "숨김")}
                    </span>
                    <span className="text-[10px] text-gray-300">·</span>
                    <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" style={{ backgroundColor: banner.slogan_color ?? "#fff" }} />
                    <span className="text-[10px] text-gray-400">{banner.slogan_font_size ?? 36}px</span>
                  </div>
                  <p className="text-sm font-semibold text-gray-800 truncate">{banner.slogan_vi}</p>
                  {banner.slogan_kr && <p className="text-xs text-gray-400 truncate">{banner.slogan_kr}</p>}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => toggleActive(banner)} disabled={togglingId === banner.id}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${banner.is_active ? "bg-green-50 text-green-600 hover:bg-green-100" : "bg-gray-50 text-gray-400 hover:bg-gray-100"}`}>
                    {togglingId === banner.id ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className={banner.is_active ? "ri-eye-line text-sm" : "ri-eye-off-line text-sm"} />}
                  </button>
                  <button
                    onClick={() => { setEditItem(banner); setShowEditor(true); }}
                    className="w-8 h-8 flex items-center justify-center bg-[#004A99]/10 text-[#004A99] rounded-lg hover:bg-[#004A99]/20 transition-colors cursor-pointer"
                    title={t("Mở Visual Editor", "Visual Editor 열기")}
                  >
                    <i className="ri-magic-line text-sm" />
                  </button>
                  <button onClick={() => handleDelete(banner.id)} disabled={deletingId === banner.id}
                    className="w-8 h-8 flex items-center justify-center bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors cursor-pointer disabled:opacity-50">
                    {deletingId === banner.id ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-delete-bin-line text-sm" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroBannerManager;
